<template>
  <div>
    <v-layout class="xs12 px-6 py-6">
      <v-flex class="xs12 d-flex mb-0">
        <div class="pr-4">
          <div class="title mb-2">Google Reviews</div>
          <div v-if="!loading">
            <div class="mb-2">
              <div class="text-uppercase caption">Show Google Rating</div>
              <div>
                <span
                  @click="openModal"
                  style="cursor: pointer;"
                  class="subheading primary--text"
                >{{ `${Boolean(Number(googleReviews)) ? 'Yes' : 'No'}` }}
                </span>
              </div>
            </div>
            <div class="mb-2">
              <div class="text-uppercase caption">Google Place ID</div>
              <div>
                <span
                  @click="openModal"
                  class="pointer"
                >
                  <span
                    v-if="!no_data.includes(googlePlaceId)"
                    class="subheading primary--text"
                  >{{ googlePlaceId }}
                  </span>
                  <em v-else class="grey--text">No data</em>
                </span>
              </div>
            </div>
            <div>
              <div class="text-uppercase caption">Show 5* Reviews</div>
              <div>
                <span
                  @click="openModal"
                  style="cursor: pointer;"
                  class="subheading primary--text"
                >{{ `${Boolean(Number(positiveGoogleReviews)) ? 'Yes' : 'No'}` }}
                </span>
              </div>
            </div>
          </div>
          <v-skeleton-loader
            v-else
            class="mt-2"
            type="heading"
            width="300px"
          ></v-skeleton-loader>
          <div class="mt-5">
            <em>Improve visitor trust with Google reviews. You may display your Google rating and reviews if you specify your Google Place ID. To obtain Google Place ID, your Google Business Page must use a street address and not use service area.</em>
          </div>
        </div>
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn 
        @click="openModal"
        sx12
        class="mr-0 mt-0"
        style="max-width: 130px;"
        color="primary" 
        dark
      >
        Edit
      </v-btn>
    </v-layout>

    <v-divider></v-divider>

    <v-dialog v-if="showDialog" v-model="showDialog" max-width="500px" persistent>
      <v-card class="pa-4">
        <v-card-title class="px-4">
            <h2 class="title">Edit Google Reviews</h2>
        </v-card-title>
        <div class="pb-4 px-6">
          <v-flex class="px-1">
            <v-switch
              label="Show Google Rating"
              v-model="preparedData.google_reviews"
              true-value="1"
              false-value="0"
            >
            </v-switch>
          </v-flex>
          <v-flex class="px-1">
            <div
              class="pa-2 mt-2 mb-4 rounded"
              :style="{ border: `solid 1px ${this.$vuetify.theme.themes.light.info}` }"
            >
              <div class="mb-3 info--text">
                <v-layout class="align-center fill-height">
                  <v-icon color="info" class="mr-2 d-flex">info</v-icon>
                  <span class="d-flex">Google Place ID Instructions</span>
                </v-layout>
              </div>
              <div class="pl-1 text-body-2">
                <div class="mb-2">
                  <b>Step 1</b> - Visit link <a target="_blank" href="https://developers.google.com/places/place-id">here</a>.
                </div>
                <div class="mb-2">
                  <b>Step 2</b> - Enter your business location. A location drop pin will display your Place ID.
                </div>
                <div>
                  <b>Step 3.</b> - Copy the Place ID: xxxxxxxxxxxxxxxxxxxx and you will need to <b>paste</b> it in the <b>Google Place ID</b> field.
                </div>
              </div>
            </div>
          </v-flex>
          <v-flex class="px-1">
            <v-text-field
              label="Google Place ID"
              v-model="preparedData.google_place_id"
              placeholder="Please Enter"
              clearable
              :error-messages="errors($v.preparedData.google_place_id || server_errors.google_place_id)"
              @blur="$v.preparedData.google_place_id.$touch()"
              @focus="resetServerValidation"
            >
            </v-text-field>
          </v-flex>
          <v-flex class="px-1">
            <v-switch
              label="Show 5* Reviews"
              v-model="preparedData.positive_google_reviews"
              true-value="1"
              false-value="0"
            >
            </v-switch>
          </v-flex>
          <v-alert 
            class="pa-2 mt-4"
            :value="$v.$error"
            outlined
            color="error"
            icon="priority_high"
            transition="scale-transition"
          >
            <span class="caption">Please fill out form correctly</span>    
          </v-alert>
          <v-alert class="pa-2 mt-4" :value="!serverValidation" outlined color="error" icon="priority_high" transition="scale-transition">
            <span class="caption">{{ serverValidationMessage }}</span>
          </v-alert>
        </div>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="closeModal">Cancel</v-btn>
            <v-btn color="primary" :loading="saveLoading" :disabled="saveLoading" @click="saveChanges">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from "@/main"
import ApiService from "@/common/api.service"
import { validationMixin } from "vuelidate"
import { required } from "vuelidate/lib/validators"
import { valMessages, getValMessage } from "@/common/error-messages"
import { mapGetters } from 'vuex'

let validations = {}

export default {
  name: 'GoogleReviews',
  mixins: [validationMixin],
  validations() {
    validations = {
      preparedData: {
        google_place_id: {}
      }
    }

    if (this.preparedData.google_reviews === "1") {
      validations.preparedData.google_place_id = { required }
    }

    this.errors = getValMessage(valMessages)(validations)
    return validations
  },
  data() {
    return {
      no_data: [null, "", undefined, "null"],
      preparedData: {},
      saveLoading: false,
      showDialog: false,
      serverValidation: true,
      serverValidationMessage: "Oops something went wrong...",
      server_errors: {}
    }
  },
  methods: {
    openModal() {
      this.showDialog = true
      this.saveLoading = false
      this.preparedData = {
        google_reviews: this.googleReviews,
        google_place_id: this.googlePlaceId,
        positive_google_reviews: this.positiveGoogleReviews
      }
      this.resetServerValidation();
      this.$v.$reset()
    },
    saveChanges() {
      this.resetServerValidation()
      this.$v.$touch()

      if(!this.$v.$invalid){
        let body = {
          organization_settings: [
            {
              key: "google_reviews",
              value: this.preparedData.google_reviews
            },
            {
              key: "positive_google_reviews",
              value: this.preparedData.positive_google_reviews
            }
          ]
        };
        if (!this.no_data.includes(this.preparedData.google_place_id)) {
          body.organization_settings.push({
            key: "google_place_id",
            value: this.preparedData.google_place_id
          })
        }
        this.saveLoading = true;
        this.resetServerValidation();

        ApiService.put("/organization_settings", body)
          .then(response => {
            this.saveLoading = false;
            EventBus.$emit("notification", {
              status: "success",
              text: "Google Reviews Settings have been successfully edited."
            });
            this.$emit('done')
            this.closeModal()
          })
          .catch(({ response }) => {
            this.saveLoading = false;
            this.serverValidation = false;
            if (response.data.error) {
              this.serverValidationMessage = this.titleCase(response.data.error);
            }
            if (response.data.message) {
              this.serverValidationMessage = response.data.message;
            }
            if (response.data.errors) {
              if (typeof response.data.errors === "string") {
                this.serverValidationMessage = response.data.errors;
              } else {
                this.server_errors = response.data.errors;
              }
            }
          })
      }
    },
    closeModal() {
      this.showDialog = false
    },
    resetServerValidation() {
      this.serverValidation = true;
      this.serverValidationMessage = "Oops something went wrong...";
      this.server_errors = {};
    },
    errors: getValMessage(valMessages)(validations)
  },
  computed: {
    ...mapGetters({
      organizationSettings: 'organizationSettings',
      loading: 'organizationSettingsLoading'
    }),
    googleReviews () {
      return !this.no_data.includes(this.organizationSettings) &&
        !this.no_data.includes(this.organizationSettings.google_reviews) ?
        this.organizationSettings.google_reviews :
        '0'
    },
    googlePlaceId () {
      return !this.no_data.includes(this.organizationSettings) &&
        !this.no_data.includes(this.organizationSettings.google_place_id) ?
        this.organizationSettings.google_place_id :
        null
    },
    positiveGoogleReviews () {
      return !this.no_data.includes(this.organizationSettings) &&
        !this.no_data.includes(this.organizationSettings.positive_google_reviews) ?
        this.organizationSettings.positive_google_reviews :
        '0'
    }
  }
}
</script>